import { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../../../../store/hooks'
import { createCommentSaga } from '../../../../../../../../../store/comments/commentsSlice'
import { setFocusedEditor } from '../../../../../../../../../store/ui/uiSlice'
import { selectAuth } from '../../../../../../../../../store/auth/authSelectors'
import { useTask } from '../../../../../../../../../contexts/TaskContext'
import { uploadAttachment } from '../../../../../../../../../lib/uploadAttachment'
import TextEditor from '../../../../../../../../../components/app/TextEditor/LazyTextEditor'
import TaskModalCommentsAddCommentSubmit from '../TaskModalCommentsAddCommentSubmit/TaskModalCommentsAddCommentSubmit'
import css from './TaskModalCommentsAddCommentEditor.module.css'

interface TaskModalCommentsAddCommentEditorProps {
    onClose: () => void
    // False while the editor is pre-warmed hidden (mounted but not yet the focused composer),
    // so spinning up Plate happens on hover instead of adding lag/jump on focus.
    active?: boolean
}

const TaskModalCommentsAddCommentEditor = ({ onClose, active = true }: TaskModalCommentsAddCommentEditorProps) => {
    const dispatch = useAppDispatch()
    const { task } = useTask()
    const currentUserId = useAppSelector(state => state.auth?.id)
    const authUser = useAppSelector(selectAuth)

    const draftKey = task ? `comment-draft-${task.id}` : null

    useEffect(() => {
        return () => {
            dispatch(setFocusedEditor(null))
        }
    }, [])

    const [editorKey, setEditorKey] = useState(0)
    // Re-read the saved draft on every editorKey (it bumps after posting). The editor now stays
    // mounted across blur (pre-warmed), so a once-captured draft would resurrect the old text
    // after posting clears localStorage — keying the read to editorKey avoids that.
    const initialDraft = useMemo<unknown>(() => {
        if (!draftKey) return undefined
        try {
            const saved = localStorage.getItem(draftKey)
            return saved ? JSON.parse(saved) : undefined
        } catch {
            return undefined
        }
    }, [draftKey, editorKey])
    const latestValueRef = useRef<unknown>(initialDraft)

    const handleImageUpload = useCallback(async (file: File) => {
        if (!task || !authUser) throw new Error('Not ready')
        return uploadAttachment({ file, workspaceId: task.workspaceId, userId: authUser.id, taskId: task.id, projectId: task.projectId })
    }, [task, authUser])

    const handleChangeImmediate = useCallback((value: unknown) => {
        latestValueRef.current = value
    }, [])

    const handleSave = useCallback((value: unknown) => {
        if (draftKey) {
            localStorage.setItem(draftKey, JSON.stringify(value))
        }
    }, [draftKey])

    const handlePost = useCallback(() => {
        const value = latestValueRef.current
        if (!value || !task || !currentUserId) return

        const nodes = value as Array<{ children: Array<{ text: string }> }>
        const isEmpty = nodes.length === 1 &&
            nodes[0].children?.length === 1 &&
            nodes[0].children[0].text === ''

        if (isEmpty) return

        dispatch(createCommentSaga({
            taskId: task.id,
            workspaceId: task.workspaceId,
            authorId: currentUserId,
            content: value
        }))

        if (draftKey) localStorage.removeItem(draftKey)
        latestValueRef.current = undefined
        setEditorKey(k => k + 1)
        onClose()
    }, [dispatch, task, currentUserId, onClose])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            handlePost()
        }
        if (e.key === 'Escape') {
            e.stopPropagation()
            onClose()
        }
    }, [handlePost, onClose])

    return (
        <div
            className={css.container}
            onKeyDown={handleKeyDown}
            data-testid="comment-editor"
        >
            <div>
                <TextEditor
                    key={editorKey}
                    value={initialDraft}
                    placeholder="Leave a comment..."
                    autoFocus
                    active={active}
                    onSave={handleSave}
                    onChangeImmediate={handleChangeImmediate}
                    onFileUpload={authUser ? handleImageUpload : undefined}
                />
            </div>
            <div className={css.footer}>
                <div className={css.footerRight}>
                    <TaskModalCommentsAddCommentSubmit onClick={handlePost} />
                </div>
            </div>
        </div>
    )
}

export default TaskModalCommentsAddCommentEditor
